import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Upload, AlertTriangle, Wand2, ShieldCheck } from "lucide-react";
import { HoloCard, NeonButton } from "@/components/Holo";
import IdentityControls, { IDENTITY_DEFAULTS } from "@/components/IdentityControls";
import ReferenceQuality from "@/components/ReferenceQuality";
import { downloadIdentityReport } from "@/lib/report";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const fileToDataUrl = (file) => new Promise((res, rej) => {
  const r = new FileReader();
  r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file);
});

const PRESETS = [
  "Cinematic product shot on matte black podium, neon rim lighting, luxury cyberpunk studio",
  "Fashion editorial portrait, holographic makeup, electric turquoise backlight, 8k",
  "Futuristic city billboard advertisement, rain-soaked neon street, Tron aesthetic",
];

export default function AILab() {
  const [prompt, setPrompt] = useState("");
  const [refImg, setRefImg] = useState(null);
  const [characters, setCharacters] = useState([]);
  const [refItemId, setRefItemId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [simFail, setSimFail] = useState(false);
  const [identity, setIdentity] = useState(IDENTITY_DEFAULTS);
  const fileRef = useRef();
  const { refreshUser } = useAuth();

  const load = async () => {
    try {
      const [v, g, o] = await Promise.all([api.get("/vault?kind=character"), api.get("/generations"), api.get("/ai/options")]);
      setCharacters(v.data); setHistory(g.data);
      if (o.data?.defaults) setIdentity((prev) => ({ ...prev, ...o.data.defaults }));
    } catch (e) { console.error("AILab load failed:", e); }
  };
  useEffect(() => { load(); }, []);

  const pick = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setRefImg(await fileToDataUrl(f)); setRefItemId(null);
  };

  const generate = async () => {
    if (!prompt.trim()) return toast.error("Enter a prompt");
    setLoading(true); setResult(null);
    const payload = { prompt, module: "ai_lab", simulate_fail: simFail, identity_settings: identity };
    if (refImg) payload.reference_image = refImg.split(",")[1];
    else if (refItemId) payload.reference_item_id = refItemId;
    try {
      const { data } = await api.post("/generate", payload);
      setResult(data.generation);
      await refreshUser();
      if (data.generation.status === "failed")
        toast.warning("Generation failed — Credit Shield refunded your credits");
      else toast.success("Generation complete");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="module-ai_lab">
      <div className="mb-8">
        <h1 className="font-display font-bold text-2xl sm:text-3xl">AI Laboratory</h1>
        <p className="font-mono-tech text-white/40 text-sm mt-1">Generate consistency-locked visuals · 5 credits per render</p>
      </div>

      <div className="grid lg:grid-cols-[1fr_1fr] gap-6">
        <HoloCard accent="turquoise" className="p-6" hover={false}>
          <span className="font-mono-tech text-[10px] uppercase tracking-widest text-white/50">Prompt</span>
          <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={4} data-testid="ailab-prompt"
            placeholder="Describe the scene you want to render..."
            className="mt-2 w-full bg-black/30 border border-white/15 focus:border-neon-turquoise focus:outline-none px-3 py-2 text-white font-mono-tech text-sm resize-none" />

          <div className="flex flex-wrap gap-2 mt-3">
            {PRESETS.map((p, i) => (
              <button key={i} onClick={() => setPrompt(p)} data-testid={`preset-${i}`}
                className="font-mono-tech text-[10px] text-white/50 border border-white/10 px-2 py-1 hover:border-neon-turquoise hover:text-neon-turquoise transition-colors text-left">{p.slice(0, 34)}…</button>
            ))}
          </div>

          <div className="mt-6">
            <span className="font-mono-tech text-[10px] uppercase tracking-widest text-white/50">Identity Lock (optional)</span>
            <div className="flex flex-wrap gap-2 mt-2">
              {characters.length === 0 && <span className="font-mono-tech text-xs text-white/30">No characters in vault yet.</span>}
              {characters.map((ch) => (
                <button key={ch.id} onClick={() => { setRefItemId(refItemId === ch.id ? null : ch.id); setRefImg(null); }}
                  data-testid={`ailab-char-${ch.id}`}
                  className={`font-mono-tech text-[10px] uppercase px-2 py-1 border transition-colors ${refItemId === ch.id ? "border-neon-turquoise text-neon-turquoise" : "border-white/15 text-white/50"}`}>
                  {ch.name}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3 mt-5">
            <button onClick={() => fileRef.current?.click()} data-testid="ailab-upload-btn"
              className="font-mono-tech text-[11px] flex items-center gap-2 border border-white/15 px-3 py-2 text-white/60 hover:border-neon-turquoise hover:text-neon-turquoise transition-colors">
              <Upload size={13} /> {refImg ? "Reference set" : "Upload reference"}
            </button>
            {refImg && <img src={refImg} alt="ref" className="w-10 h-10 object-cover border border-white/20" />}
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={pick} />
          </div>
          {refImg && <ReferenceQuality image={refImg} testid="ailab-ref-quality" />}

          <div className="mt-6 pt-5 border-t border-white/10">
            <IdentityControls value={identity} onChange={setIdentity} accent="turquoise" testid="ailab-identity" />
          </div>

          <label className="flex items-center gap-2 mt-5 font-mono-tech text-[10px] text-white/40 cursor-pointer" data-testid="ailab-simfail">
            <input type="checkbox" checked={simFail} onChange={(e) => setSimFail(e.target.checked)} />
            <AlertTriangle size={12} /> Simulate failure (test Credit Shield refund)
          </label>

          <NeonButton accent="pink" onClick={generate} disabled={loading} className="w-full mt-6" data-testid="ailab-generate-btn">
            {loading ? <><Wand2 size={14} className="animate-pulse-glow" /> Rendering...</> : <><Sparkles size={14} /> Generate (5 credits)</>}
          </NeonButton>
        </HoloCard>

        <HoloCard accent="pink" className="p-4 min-h-[360px] flex items-center justify-center" hover={false} testid="ailab-output">
          {loading ? (
            <div className="text-center font-mono-tech text-neon-turquoise text-sm">
              <div className="w-12 h-12 border-2 border-neon-turquoise border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              NEURAL RENDER IN PROGRESS...
            </div>
          ) : result?.image ? (
            <img src={result.image} alt="result" className="w-full h-full object-contain max-h-[440px]" data-testid="ailab-result-img" />
          ) : result?.status === "failed" ? (
            <div className="text-center font-mono-tech text-neon-pink text-sm px-6">
              <AlertTriangle className="mx-auto mb-3" /> Generation failed.<br />Credit Shield refunded 5 credits.
            </div>
          ) : (
            <div className="text-center font-mono-tech text-white/30 text-sm"><Sparkles className="mx-auto mb-2" /> Output renders here</div>
          )}
        </HoloCard>
      </div>

      {result?.identity && result.identity.status !== "NO_FACE" && (
        <div className="mt-6" data-testid="ailab-identity-result">
          <HoloCard accent={result.identity.status === "LOCKED" ? "green" : "pink"} className="p-5" hover={false}>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <ShieldCheck size={18} style={{ color: result.identity.status === "LOCKED" ? "#39ff14" : "#ff007f" }} />
                <div>
                  <div className="font-mono-tech text-[10px] uppercase tracking-widest text-white/50">Biometric Identity Match</div>
                  <div className="font-display font-black text-2xl" style={{ color: result.identity.status === "LOCKED" ? "#39ff14" : "#ff007f" }}>
                    {result.identity.overall}% · {result.identity.status}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {Object.entries(result.identity.scores || {}).map(([k, v]) => (
                  <div key={k} className="border border-white/10 px-2 py-1 text-center" data-testid={`ailab-metric-${k}`}>
                    <div className="font-display font-bold text-sm" style={{ color: v >= 75 ? "#39ff14" : "#ff007f" }}>{v}%</div>
                    <div className="font-mono-tech text-[8px] uppercase text-white/40 leading-tight">{k.replace(" Match", "").replace(" Preservation", "")}</div>
                  </div>
                ))}
              </div>
            </div>
            {result.refunded && (
              <div className="mt-3 font-mono-tech text-[10px] text-neon-pink flex items-center gap-2"><AlertTriangle size={12} /> Identity drift detected — Credit Shield refunded this render.</div>
            )}
            {result.identity.flags?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {result.identity.flags.map((f) => (
                  <span key={f} className="font-mono-tech text-[9px] uppercase tracking-wide border border-neon-pink/40 text-neon-pink px-2 py-0.5">{f}</span>
                ))}
              </div>
            )}
            <p className="font-mono-tech text-[8px] text-white/25 mt-2">{result.identity.note}</p>
            <div className="mt-3">
              <NeonButton accent="turquoise" onClick={() => downloadIdentityReport({ image: result.image, identity: result.identity, prompt })} data-testid="download-report-btn">
                <ShieldCheck size={13} /> Download Identity Report
              </NeonButton>
            </div>
          </HoloCard>
        </div>
      )}

      {history.length > 0 && (
        <div className="mt-10">
          <h2 className="font-mono-tech text-white/50 text-xs uppercase tracking-widest mb-4">// Recent Renders</h2>
          <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3">
            {history.filter((h) => h.image).slice(0, 12).map((h) => (
              <div key={h.id} className="aspect-square border border-white/10 overflow-hidden" data-testid={`gen-${h.id}`}>
                <img src={h.image} alt="gen" className="w-full h-full object-cover hover:scale-105 transition-transform" />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
